import { readSheet } from "read-excel-file/browser";

import { DeviceStatusRow } from "components/rules/deviceStatusRow";
import { isGrade } from "domain/grade";

export const DEVICE_STATUS_IMPORT_HEADERS = ["manufacturer", "model", "blancco_grade", "value"] as const;

// Files created before the column was renamed to blancco_grade still use this header.
const LEGACY_GRADE_HEADER = "grade";

// Several uplift values fit in one CSV cell when separated by this character.
const UPLIFT_VALUE_SEPARATOR = ";";

export type ImportedDeviceStatusRow = Omit<DeviceStatusRow, "id">;

export interface DeviceStatusImportResult {
    rows: ImportedDeviceStatusRow[];
    invalidRowCount: number;
}

const toCell = (record: Record<string, string>, header: string) => {
    const key = Object.keys(record).find((candidate) => candidate.trim().toLowerCase() === header);
    return key === undefined ? "" : record[key].trim();
};

const parseUpliftUuids = (cell: string, uuidByHeadline: Record<string, string>) =>
    cell
        .split(UPLIFT_VALUE_SEPARATOR)
        .map((headline) => uuidByHeadline[headline.trim().toLowerCase()])
        .filter((uuid): uuid is string => uuid !== undefined);

// Unknown uplift headlines are ignored so that one stale name does not discard an otherwise valid rule.
export const parseDeviceStatusImport = (
    records: Record<string, string>[],
    uuidByHeadline: Record<string, string>
): DeviceStatusImportResult => {
    const rows: ImportedDeviceStatusRow[] = [];
    let invalidRowCount = 0;
    records.forEach((record) => {
        const manufacturer = toCell(record, "manufacturer");
        const model = toCell(record, "model");
        const grade = (toCell(record, "blancco_grade") || toCell(record, LEGACY_GRADE_HEADER)).toUpperCase();
        const value = toCell(record, "value");
        const isValid =
            manufacturer !== "" &&
            model !== "" &&
            value !== "" &&
            !Number.isNaN(Number(value)) &&
            (grade === "" || isGrade(grade));
        if (!isValid) {
            invalidRowCount++;
            return;
        }
        rows.push({
            manufacturer,
            model,
            blanccoGrade: isGrade(grade) ? grade : undefined,
            value,
            upliftValues: parseUpliftUuids(toCell(record, "uplift_values"), uuidByHeadline),
            isEditing: false,
            isCreated: false,
            validated: true,
        });
    });
    return { rows, invalidRowCount };
};

// Beyond this many rows the table, which holds all rules in memory and edits them inline, becomes unwieldy.
export const MAX_IMPORT_ROWS = 5_000;

export class DeviceStatusImportFileError extends Error {
    constructor(public readonly reason: "emptyFile" | "invalidFileType" | "limitExceeded") {
        super(reason);
    }
}

const toRecords = (headers: string[], dataRows: string[][]) =>
    dataRows
        .filter((cells) => cells.some((cell) => cell.trim() !== ""))
        .map((cells) => Object.fromEntries(headers.map((header, index) => [header, cells[index] ?? ""])));

// Tokenize according to RFC 4180: quoted fields may contain commas, line breaks and escaped quotes (""). A hand-written
// tokenizer avoids adding a dependency for the single place that reads CSV here.
export const parseCsv = (text: string): string[][] => {
    const rows: string[][] = [];
    let cells: string[] = [];
    let cell = "";
    let isQuoted = false;
    const endCell = () => {
        cells.push(cell.trim());
        cell = "";
    };
    const endRow = () => {
        endCell();
        rows.push(cells);
        cells = [];
    };
    for (let index = 0; index < text.length; index++) {
        const character = text[index];
        if (isQuoted) {
            if (character !== '"') {
                cell += character;
            } else if (text[index + 1] === '"') {
                cell += '"';
                index++;
            } else {
                isQuoted = false;
            }
        } else if (character === '"') {
            isQuoted = true;
        } else if (character === ",") {
            endCell();
        } else if (character === "\n" || character === "\r") {
            if (character === "\r" && text[index + 1] === "\n") {
                index++;
            }
            endRow();
        } else {
            cell += character;
        }
    }
    // A trailing line break does not start a new row.
    if (cell !== "" || cells.length > 0) {
        endRow();
    }
    return rows;
};

const readXlsxRows = async (file: File) =>
    (await readSheet(file)).map((row) => row.map((cell) => String(cell ?? "").trim()));

// Read the file as a header row followed by data rows. Throw DeviceStatusImportFileError for unusable files.
export const readDeviceStatusImportFile = async (file: File): Promise<Record<string, string>[]> => {
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith(".csv") && !fileName.endsWith(".xlsx")) {
        throw new DeviceStatusImportFileError("invalidFileType");
    }
    const [headers, ...dataRows] = fileName.endsWith(".xlsx") ? await readXlsxRows(file) : parseCsv(await file.text());
    const records = headers === undefined ? [] : toRecords(headers, dataRows);
    if (records.length === 0) {
        throw new DeviceStatusImportFileError("emptyFile");
    }
    if (records.length > MAX_IMPORT_ROWS) {
        throw new DeviceStatusImportFileError("limitExceeded");
    }
    return records;
};
